import { min, sortBy, trimArray } from 'lib/array/arrayUtils'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { TOUR_V2_TARGET_OCCUPANT_TYPES } from 'constants/tours'
import createSelector from 'lib/web/createSelector'
import generateDepartureRoomPriceBreakdown from 'lib/tours/generateDepartureRoomPriceBreakdown'

import { getOccupancies, getRoomSizes } from './tourV2OfferPageTravellersSelectors'
import {
  checkPurchasableOptionHasPackageUpgradeOrRoomType, findDiscountOnTour,
  getRoomTypeCapacity,
} from 'lib/tours/tourUtils'
import { isLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'

export const getDeparturesAccommodableOptions = createSelector(
  (offerPageState: OfferPageState) => offerPageState.tourV2Offer.purchasableOptions,
  (offerPageState: OfferPageState) => offerPageState.tourV2Offer.departures,
  (offerPageState: OfferPageState) => offerPageState.tourV2Offer.roomTypePricing,
  (offerPageState: OfferPageState) => offerPageState.selectedTourV2OfferVariation,
  getRoomSizes,
  (
    purchasableOptions,
    departures,
    roomTypePricing,
    selectedVariation,
    roomSizes,
  ): Map<Tours.TourV2OfferDeparture, Tours.V2OfferAccommodablePurchasableOptions> => {
    const departuresAccommodableOptions = new Map<Tours.TourV2OfferDeparture, Tours.V2OfferAccommodablePurchasableOptions>()

    for (const purchasableOption of purchasableOptions) {
      // matches the selected tour option ID
      if (purchasableOption.fkVariationId === selectedVariation.id) {
        const roomTypeCapacity = getRoomTypeCapacity(purchasableOption.roomType)
        const departure = departures[purchasableOption.fkDepartureId]

        if (!roomSizes.has(roomTypeCapacity) && !departure.privateRequestKey) continue // purchasable option is not accommodable for non private departure

        if (!departuresAccommodableOptions.has(departure)) {
          departuresAccommodableOptions.set(departure, new Map())
        }

        const departureAccommodableOptions = departuresAccommodableOptions.get(departure)

        const roomTypePrice = roomTypePricing[purchasableOption.fkRoomTypePricingId]

        if (!departureAccommodableOptions.has(roomTypeCapacity)) {
          departureAccommodableOptions.set(roomTypeCapacity, new Map())
        }

        const accommodableOption = departureAccommodableOptions.get(roomTypeCapacity)

        accommodableOption.set(purchasableOption, roomTypePrice)
      }
    }

    const roomSizeValues = Array.from(roomSizes.values())

    // remove departures that don't have at least one accommodable option per room size
    for (const [departure, departureAccommodableOptions] of departuresAccommodableOptions) {
      // remove departure if not accommodable by every travelling party size / don't remove private departure
      if (!departure.privateRequestKey &&
        roomSizeValues.some(roomSize => (
          !departureAccommodableOptions.has(roomSize) ||
          departureAccommodableOptions.get(roomSize).size === 0
        ))) {
        departuresAccommodableOptions.delete(departure)
      }
    }

    return departuresAccommodableOptions
  },
)

export const getDeparturesCheapestPurchasableOptions = createSelector(
  getDeparturesAccommodableOptions,
  (offerPageState: OfferPageState, state: App.State) => isLuxPlusToursEnabled(state),
  (
    departuresAccommodableOptions,
    luxPlusEnabled,
  ): Map<Tours.TourV2OfferDeparture, Tours.TourV2OfferPurchasableOption> => Array.from(departuresAccommodableOptions.entries()).reduce<Map<Tours.TourV2OfferDeparture, Tours.TourV2OfferPurchasableOption>>(
    (
      departuresCheapestPurchasableOption,
      [departure, departureAccommodableOptions],
    ) => {
      const cheapestAccommodableOptions = Array.from(departureAccommodableOptions.values()).map(accommodableOptions => min(
        Array.from(accommodableOptions.keys()),
        (purchasableOption) => luxPlusEnabled ? purchasableOption.memberPrice || purchasableOption.price : purchasableOption.price,
      ))
      const cheapestPurchasableOption = min(
        cheapestAccommodableOptions,
        (purchasableOption) => luxPlusEnabled ? purchasableOption.memberPrice || purchasableOption.price : purchasableOption.price,
      )

      if (cheapestPurchasableOption) {
        departuresCheapestPurchasableOption.set(departure, cheapestPurchasableOption)
      }

      return departuresCheapestPurchasableOption
    },
    new Map(),
  ),
)

const MONTH_INDICES: Array<App.MonthIndex> = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]

export const getCategorisedDepartures = createSelector(
  (offerPageState: OfferPageState) => offerPageState.tourV2Offer,
  getDeparturesAccommodableOptions,
  (
    tourV2Offer,
    departuresAccommodableOptions,
  ): Tours.V2OfferCategorisedDepartures => {
    const categorisedDepartures: Tours.V2OfferCategorisedDepartures = new Map()

    for (const departure of departuresAccommodableOptions.keys()) {
      const date = new Date(departure.startDate)
      const departureYear = date.getFullYear()
      const departureMonthIndex = date.getMonth() as App.MonthIndex

      if (!categorisedDepartures.has(departureYear)) {
        categorisedDepartures.set(departureYear, new Map())

        for (const monthIndex of MONTH_INDICES) {
          categorisedDepartures.get(departureYear).set(monthIndex, { availableDepartures: [], soldOutDepartures: [], hasDiscount: false })
        }
      }

      if (departure.status === 'available') {
        categorisedDepartures.get(departureYear).get(departureMonthIndex).availableDepartures.push(departure)

        // Calculate discount for the month base on departure
        const hasDiscount = categorisedDepartures.get(departureYear).get(departureMonthIndex).hasDiscount
        const { hasSomeDiscount, allIsDiscounted } = findDiscountOnTour(tourV2Offer, undefined, [departure.id])
        categorisedDepartures.get(departureYear).get(departureMonthIndex).hasDiscount = hasDiscount || hasSomeDiscount || allIsDiscounted
      } else if (departure.status !== 'hidden') {
        categorisedDepartures.get(departureYear).get(departureMonthIndex).soldOutDepartures.push(departure)
      }
    }

    return categorisedDepartures
  },
)

export const getDepartureYearsDisplayableMonthIndices = createSelector(
  getCategorisedDepartures,
  (
    categorisedDepartures,
  ): Map<number, Array<App.MonthIndex>> => {
    const departureYearsDisplayableMonthIndices = new Map<number, Array<App.MonthIndex>>()
    const sortedCategorisedDepartures = sortBy(
      categorisedDepartures.entries(),
      ([departureYear]) => departureYear,
      'asc',
    )

    for (const [departureYear, monthDepartures] of sortedCategorisedDepartures) {
      departureYearsDisplayableMonthIndices.set(departureYear, trimArray(
        Array.from(monthDepartures.keys()),
        (monthIndex) => {
          const {
            availableDepartures,
            soldOutDepartures,
          } = categorisedDepartures.get(departureYear).get(monthIndex)

          return availableDepartures.length === 0 && soldOutDepartures.length === 0
        },
        'both-ends',
      ))
    }

    return departureYearsDisplayableMonthIndices
  },
)

export const getDepartureYears = createSelector(
  getDepartureYearsDisplayableMonthIndices,
  (
    departureYearsDisplayableMonthIndices,
  ): Array<number> => [...departureYearsDisplayableMonthIndices.keys()],
)

export interface MonthStartingPriceBreakdown {
  price: number;
  isMemberPrice?: boolean;
}

export const getAvailableMonthsStartingPrices = createSelector(
  (offerPageState: OfferPageState) => offerPageState.selectedDepartureYear,
  getCategorisedDepartures,
  getDeparturesCheapestPurchasableOptions,
  getDepartureYearsDisplayableMonthIndices,
  (offerPageState: OfferPageState, state: App.State) => isLuxPlusToursEnabled(state),
  (
    selectedDepartureYear,
    categorisedDepartures,
    departuresCheapestPurchasableOptions,
    departureYearsDisplayableMonthIndices,
    luxPlusEnabled,
  ): Map<App.MonthIndex, MonthStartingPriceBreakdown> => {
    const availableMonthsStartingPrices = new Map<App.MonthIndex, MonthStartingPriceBreakdown>()

    if (!categorisedDepartures.has(selectedDepartureYear)) {
      return availableMonthsStartingPrices
    }

    for (const monthIndex of departureYearsDisplayableMonthIndices.get(selectedDepartureYear)) {
      const departures = categorisedDepartures.get(selectedDepartureYear).get(monthIndex).availableDepartures
      const cheapestDepartures = departures.map(dep => departuresCheapestPurchasableOptions.get(dep))
      const startingPrice = min(
        cheapestDepartures,
        departure => (luxPlusEnabled ? departure?.memberPrice || departure?.price : departure?.price) ?? 0,
      )

      availableMonthsStartingPrices.set(
        monthIndex,
        {
          price: (luxPlusEnabled ? startingPrice?.memberPrice || startingPrice?.price : startingPrice?.price) ?? 0,
          isMemberPrice: luxPlusEnabled && !!startingPrice?.memberPrice,
        })
    }

    return availableMonthsStartingPrices
  },
)

export const getDeparturesOccupanciesPriceBreakdowns = createSelector(
  (offerPageState: OfferPageState) => offerPageState.selectedTourV2OfferVariation,
  getDeparturesAccommodableOptions,
  getOccupancies,
  (
    selectedVariation,
    departuresAccommodableOptions,
    occupancies,
  ): Map<Tours.TourV2OfferDeparture, Map<App.Occupants, Map<Tours.TourV2OfferPurchasableOption, Tours.V2OfferDeparturePriceBreakdown>>> => {
    const departuresOccupanciesPriceBreakdowns = new Map<Tours.TourV2OfferDeparture, Map<App.Occupants, Map<Tours.TourV2OfferPurchasableOption, Tours.V2OfferDeparturePriceBreakdown>>>()

    for (const departure of departuresAccommodableOptions.keys()) {
      if (!departuresOccupanciesPriceBreakdowns.has(departure)) {
        departuresOccupanciesPriceBreakdowns.set(departure, new Map())
      }

      const departureOccupantsPriceBreakdowns = departuresOccupanciesPriceBreakdowns.get(departure)

      const departureAccommodableOptions = departuresAccommodableOptions.get(departure)

      for (const occupants of occupancies) {
        if (!departureOccupantsPriceBreakdowns.has(occupants)) {
          departureOccupantsPriceBreakdowns.set(occupants, new Map())
        }

        const occupantsPriceBreakdowns = departureOccupantsPriceBreakdowns.get(occupants)
        // if private departure always take the only one departure whatever occupancy
        const [firstDepartureAccommodableOption] = departureAccommodableOptions.values()
        const accommodableOptions = departure.privateRequestKey ? firstDepartureAccommodableOption : departureAccommodableOptions.get(sumUpOccupancies([occupants], TOUR_V2_TARGET_OCCUPANT_TYPES))

        for (const [purchasableOption, roomTypePrice] of accommodableOptions) {
          occupantsPriceBreakdowns.set(
            purchasableOption,
            generateDepartureRoomPriceBreakdown(
              occupants,
              purchasableOption,
              roomTypePrice,
              selectedVariation,
            ),
          )
        }
      }
    }

    return departuresOccupanciesPriceBreakdowns
  },
)

export const getSelectedDepartureOccupanciesOptions = createSelector(
  (offerPageState: OfferPageState) => offerPageState.selectedTourV2OfferDeparture,
  getDeparturesOccupanciesPriceBreakdowns,
  (
    selectedDeparture,
    departuresOccupanciesPriceBreakdowns,
  ): Map<App.Occupants, Map<Tours.TourV2OfferPurchasableOption, Tours.V2OfferDeparturePriceBreakdown>> => {
    return departuresOccupanciesPriceBreakdowns.get(selectedDeparture) || new Map()
  },
)

export const getSelectableOccupancies = createSelector(
  getSelectedDepartureOccupanciesOptions,
  (
    occupanciesOptions,
  ): Array<App.Occupants> => Array.from(occupanciesOptions.keys()),
)

export const hasPackageUpgradeOrRoomTypeInSelectableOccupancies = createSelector(
  getSelectedDepartureOccupanciesOptions,
  (
    occupanciesOptions: Map<App.Occupants, Map<Tours.TourV2OfferPurchasableOption, Tours.V2OfferDeparturePriceBreakdown>>,
  ): boolean => {
    const purchasableOptions = Array.from(occupanciesOptions.values())
      .filter(occupancyOptions => occupancyOptions.size > 1)
      .flatMap<Tours.TourV2OfferPurchasableOption>(occupancyOptions => Array.from(occupancyOptions.keys()))
    const { hasPackageUpgrade, hasRoomType } = checkPurchasableOptionHasPackageUpgradeOrRoomType(purchasableOptions)
    return hasPackageUpgrade || hasRoomType
  },
)

export const getDepartureMonthIndicesToDisplay = createSelector(
  (offerPageState: OfferPageState) => offerPageState.selectedDepartureYear,
  (offerPageState: OfferPageState) => offerPageState.selectedDepartureMonthIndex,
  getDepartureYearsDisplayableMonthIndices,
  (
    selectedDepartureYear,
    selectedDepartureMonthIndex,
    departureYearsDisplayableMonthIndices,
  ): Set<App.MonthIndex> => new Set(departureYearsDisplayableMonthIndices.get(selectedDepartureYear)?.filter(monthIndex => monthIndex >= selectedDepartureMonthIndex) ?? []),
)
